/**
 * The id of the record a screen has open, as it appears in the address bar.
 *
 * Not encryption — the value travels to the person's own browser and comes back
 * from it, so anything reversible there is reversible by them. It is an opaque
 * handle: the bar reads `?view=NDIuMWM4` instead of `?view=42`, so a row's
 * position in the table is not on display and a hand-edited number does not
 * silently open somebody else's record. The API's permission checks are what
 * actually decide who may read what; this only keeps the id from being an
 * invitation.
 */

/** Enough to notice a changed digit, small enough to keep the token short. */
function checksum(id: number): string {
  let hash = 2166136261;
  for (const character of `mrm:${id}`) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).slice(0, 4);
}

export function encodeRecordId(id: number): string {
  // URL-safe base64: the address bar should not need to escape it.
  return btoa(`${id}.${checksum(id)}`)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** The id back, or null if the token was truncated, mistyped, or invented. */
export function decodeRecordId(token: string | undefined): number | null {
  if (!token) return null;
  try {
    const padded = token.replace(/-/g, "+").replace(/_/g, "/");
    const [raw, mark] = atob(padded).split(".");
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0 || mark !== checksum(id)) return null;
    return id;
  } catch {
    return null;
  }
}
